import axios from 'axios';
import { BACKEND_URL } from './config';
import { supabase } from './supabase';
import { errorHandler } from '../utils/errorHandler';
import { logger } from '../utils/logger';

const PRODUCTION_ORIGIN = 'https://livenow-app.com';

/** OAuth redirect target — always return to home (HashRouter). */
const getRedirectTarget = () => {
    if (typeof window === 'undefined') return undefined;
    const { origin } = window.location;

    const stayOnOrigin =
        origin.includes('localhost')
        || origin.includes('127.0.0.1')
        || origin.endsWith('.vercel.app')
        || origin.includes('livenow-app.com');

    const redirectOrigin = stayOnOrigin ? origin : PRODUCTION_ORIGIN;
    return `${redirectOrigin}/#/`;
};

const inputValidation = {
    validateEmail: (email) => {
        if (!email) return { isValid: false, error: 'Email is required' };
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return { isValid: false, error: 'Please enter a valid email address' };
        }
        
        const disposableDomains = ['tempmail.org', '10minutemail.com', 'guerrillamail.com'];
        const domain = email.split('@')[1];
        if (disposableDomains.includes(domain)) {
            return { isValid: false, error: 'Please use a valid email address' };
        }
        
        return { isValid: true, error: null };
    },

    validatePassword: (password) => {
        if (!password) return { isValid: false, error: 'Password is required' };
        
        if (password.length < 8) {
            return { isValid: false, error: 'Password must be at least 8 characters long' };
        }
        
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        
        const strengthScore = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
        
        if (strengthScore < 3) {
            return { 
                isValid: false, 
                error: 'Password must contain at least 3 of: uppercase, lowercase, numbers, special characters' 
            };
        }
        
        return { isValid: true, error: null };
    },

    sanitizeInput: (input) => {
        if (typeof input !== 'string') return '';
        
        return input
            .trim()
            .replace(/[<>]/g, '')
            .replace(/javascript:/gi, '')
            .replace(/on\w+=/gi, '')
            .substring(0, 255);
    }
};

const rateLimiting = {
    attempts: new Map(),
    
    isRateLimited: (identifier, maxAttempts = 5, windowMs = 15 * 60 * 1000) => {
        const now = Date.now();
        const userAttempts = rateLimiting.attempts.get(identifier) || [];
        
        const recentAttempts = userAttempts.filter(timestamp => now - timestamp < windowMs);
        
        if (recentAttempts.length >= maxAttempts) {
            return { 
                isLimited: true, 
                remainingTime: Math.ceil((recentAttempts[0] + windowMs - now) / 1000 / 60)
            };
        }
        
        return { isLimited: false, remainingTime: 0 };
    },
    
    recordAttempt: (identifier) => {
        const now = Date.now();
        const userAttempts = rateLimiting.attempts.get(identifier) || [];
        userAttempts.push(now);
        rateLimiting.attempts.set(identifier, userAttempts);
    },
    
    clearAttempts: (identifier) => {
        rateLimiting.attempts.delete(identifier);
    }
};

const csrfProtection = {
    generateToken: () => {
        const token = Math.random().toString(36).substring(2) + Date.now().toString(36);
        localStorage.setItem('csrf_token', token);
        return token;
    },
    
    getToken: () => {
        return localStorage.getItem('csrf_token');
    },
    
    validateToken: (token) => {
        const storedToken = localStorage.getItem('csrf_token');
        return token === storedToken;
    },
    
    clearToken: () => {
        localStorage.removeItem('csrf_token');
    }
};

const sessionManager = {
    sessionTimeout: 60 * 1000,
    warningTime: 15 * 1000,
    refreshThreshold: 30 * 1000,
    
    getSessionExpiry: () => {
        const expiresAt = localStorage.getItem('expires_at');
        return expiresAt ? parseInt(expiresAt) * 1000 : null;
    },
    
    isSessionExpired: () => {
        const expiry = sessionManager.getSessionExpiry();
        return expiry ? Date.now() > expiry : true;
    },
    
    needsRefresh: () => {
        const expiry = sessionManager.getSessionExpiry();
        if (!expiry) return true;
        
        const timeUntilExpiry = expiry - Date.now();
        return timeUntilExpiry < sessionManager.refreshThreshold;
    },
    
    isExpiringSoon: () => {
        const expiry = sessionManager.getSessionExpiry();
        if (!expiry) return false;
        
        const timeUntilExpiry = expiry - Date.now();
        return timeUntilExpiry < sessionManager.warningTime && timeUntilExpiry > 0;
    },
    
    getRemainingTime: () => {
        const expiry = sessionManager.getSessionExpiry();
        if (!expiry) return 0;
        
        const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 1000));
        return remaining;
    },
    
    updateSessionExpiry: (expiresAt) => {
        if (expiresAt) {
            localStorage.setItem('expires_at', expiresAt.toString());
        }
    }
};

export async function refreshToken() {
    logger.sessionInfo('Token refresh attempt started');
    
    try {
        const { data, error } = await supabase.auth.refreshSession();
        
        if (error) {
            const handledError = errorHandler.handleError(error, { action: 'token_refresh' });
            logger.sessionError('Token refresh failed', { error: error.message });
            throw new Error(handledError.message);
        }
        
        if (data.session) {
            await secureStoreToken(data.session.access_token);
            
            if (data.session.expires_at) {
                sessionManager.updateSessionExpiry(data.session.expires_at);
            }
            
            logger.sessionInfo('Token refresh successful', { 
                expiresAt: data.session.expires_at 
            });
            
            return data.session;
        } else {
            const error = new Error('No session returned from refresh');
            const handledError = errorHandler.handleError(error, { action: 'token_refresh' });
            logger.sessionError('Token refresh returned no session');
            throw new Error(handledError.message);
        }
    } catch (error) {
        if (!error.type) {
            const handledError = errorHandler.handleError(error, { action: 'token_refresh' });
            throw new Error(handledError.message);
        }
        
        throw error;
    }
}

export async function checkAndRefreshToken() {
    logger.sessionInfo('Checking if token refresh is needed');
    
    if (sessionManager.needsRefresh()) {
        logger.sessionInfo('Token refresh needed, attempting refresh');
        try {
            await refreshToken();
            logger.sessionInfo('Token refresh completed successfully');
            return true;
        } catch (error) {
            logger.sessionError('Token refresh failed during check', { error: error.message });
            return false;
        }
    } else {
        logger.sessionInfo('Token refresh not needed');
        return true;
    }
}

export function getSessionStatus() {
    const status = {
        isExpired: sessionManager.isSessionExpired(),
        needsRefresh: sessionManager.needsRefresh(),
        isExpiringSoon: sessionManager.isExpiringSoon(),
        remainingTime: sessionManager.getRemainingTime(),
        expiryTime: sessionManager.getSessionExpiry()
    };
    
    logger.sessionInfo('Session status checked', status);
    return status;
}

export async function signup(email, password, options = {}) {
    logger.authInfo('Signup attempt started', { email: email ? 'provided' : 'missing' });
    
    try {
        const sanitizedEmail = inputValidation.sanitizeInput(email);
        const sanitizedPassword = inputValidation.sanitizeInput(password);
        
        const emailValidation = inputValidation.validateEmail(sanitizedEmail);
        const passwordValidation = inputValidation.validatePassword(sanitizedPassword);
        
        if (!emailValidation.isValid) {
            const error = new Error(emailValidation.error);
            const handledError = errorHandler.handleError(error, { 
                action: 'signup', 
                field: 'email',
                sanitized: sanitizedEmail !== email 
            });
            logger.authError('Signup validation failed - email', { 
                original: email ? 'provided' : 'missing',
                sanitized: sanitizedEmail !== email 
            });
            throw new Error(handledError.message);
        }
        
        if (!passwordValidation.isValid) {
            const error = new Error(passwordValidation.error);
            const handledError = errorHandler.handleError(error, { 
                action: 'signup', 
                field: 'password',
                sanitized: sanitizedPassword !== password 
            });
            logger.authError('Signup validation failed - password', { 
                sanitized: sanitizedPassword !== password 
            });
            throw new Error(handledError.message);
        }
        
        const rateLimitCheck = rateLimiting.isRateLimited(`signup_${sanitizedEmail}`);
        if (rateLimitCheck.isLimited) {
            const error = new Error(`Too many signup attempts. Please try again in ${rateLimitCheck.remainingTime} minutes.`);
            const handledError = errorHandler.handleError(error, { 
                action: 'signup', 
                email: sanitizedEmail,
                remainingTime: rateLimitCheck.remainingTime 
            });
            logger.authError('Signup rate limited', { 
                email: sanitizedEmail, 
                remainingTime: rateLimitCheck.remainingTime 
            });
            throw new Error(handledError.message);
        }
        
        const csrfToken = csrfProtection.generateToken();
        logger.securityInfo('CSRF token generated for signup');
        
        logger.authInfo('Signup validation passed, calling backend', { email: sanitizedEmail });

        const consent = options?.consent || null;
        const response = await axios.post(`${BACKEND_URL}/auth/signup`, {
            email: sanitizedEmail,
            password: sanitizedPassword,
            ...(consent ? { consent } : {}),
        }, { timeout: 20000 });

        const data = response.data;
        if (data?.error) {
            throw new Error(data.error);
        }

        if (data?.session?.access_token && data?.session?.refresh_token) {
            await supabase.auth.setSession({
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token,
            });
        }

        rateLimiting.clearAttempts(`signup_${sanitizedEmail}`);
        logger.authInfo('Signup successful', { email: sanitizedEmail });
        return data;
    } catch (error) {
        rateLimiting.recordAttempt(`signup_${email}`);
        
        if (!error.type) {
            const handledError = errorHandler.handleError(error, { action: 'signup' });
            throw new Error(handledError.message);
        }
        
        throw error;
    }
}

export async function login(email, password) {
    logger.authInfo('Login attempt started', { email: email ? 'provided' : 'missing' });
    
    try {
        // Input validation
        const sanitizedEmail = inputValidation.sanitizeInput(email);
        const sanitizedPassword = inputValidation.sanitizeInput(password);
        
        const emailValidation = inputValidation.validateEmail(sanitizedEmail);
        if (!emailValidation.isValid) {
            const error = new Error(emailValidation.error);
            const handledError = errorHandler.handleError(error, { 
                action: 'login', 
                field: 'email',
                sanitized: sanitizedEmail !== email 
            });
            logger.authError('Login validation failed - email', { 
                original: email ? 'provided' : 'missing',
                sanitized: sanitizedEmail !== email 
            });
            throw new Error(handledError.message);
        }
        
        if (!sanitizedPassword) {
            const error = new Error('Password is required');
            const handledError = errorHandler.handleError(error, { 
                action: 'login', 
                field: 'password' 
            });
            logger.authError('Login validation failed - password missing');
            throw new Error(handledError.message);
        }
        
        const rateLimitCheck = rateLimiting.isRateLimited(`login_${sanitizedEmail}`);
        if (rateLimitCheck.isLimited) {
            const error = new Error(`Too many login attempts. Please try again in ${rateLimitCheck.remainingTime} minutes.`);
            const handledError = errorHandler.handleError(error, { 
                action: 'login', 
                email: sanitizedEmail,
                remainingTime: rateLimitCheck.remainingTime 
            });
            logger.authError('Login rate limited', { 
                email: sanitizedEmail, 
                remainingTime: rateLimitCheck.remainingTime 
            });
            throw new Error(handledError.message);
        }
        
        let csrfToken = csrfProtection.getToken();
        if (!csrfToken) {
            csrfToken = csrfProtection.generateToken();
            logger.securityInfo('CSRF token generated for login');
        }
        
        logger.authInfo('Login validation passed, calling Supabase', { email: sanitizedEmail });
        
        const { data, error } = await supabase.auth.signInWithPassword({
            email: sanitizedEmail,
            password: sanitizedPassword,
        });
        
        if (error) {
            const handledError = errorHandler.handleError(error, { 
                action: 'login', 
                email: sanitizedEmail 
            });
            logger.authError('Supabase login failed', { 
                email: sanitizedEmail, 
                error: error.message 
            });
            throw new Error(handledError.message);
        }
        
        if (data.session) {
            await secureStoreToken(data.session.access_token);
            logger.sessionInfo('Session token stored after login');
        }
        
        rateLimiting.clearAttempts(`login_${sanitizedEmail}`);
        logger.authInfo('Login successful', { email: sanitizedEmail });
        
        return data;
    } catch (error) {
        rateLimiting.recordAttempt(`login_${email}`);
        
        if (!error.type) {
            const handledError = errorHandler.handleError(error, { action: 'login' });
            throw new Error(handledError.message);
        }
        
        throw error;
    }
}

export async function signInWithGoogle() {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getRedirectTarget(),
        queryParams: {
          access_type: 'offline',
          prompt: 'select_account'
        }
      }
    });
    
    if (error) {
      throw new Error(error.message);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
}

export async function signInWithGoogleForLogin() {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getRedirectTarget(),
        queryParams: {
          access_type: 'offline',
          prompt: 'select_account'
        }
      }
    });
    
    if (error) {
      throw new Error(error.message);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
}

export async function checkUserExists(email) {
  try {
    return false;
  } catch (error) {
    return false;
  }
}

export async function logout() {
  const { error } = await supabase.auth.signOut();
  if (error) throw new Error(error.message);
  await clearStoredTokens();
}

export async function getMe() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw new Error(error.message);
  return { user };
}

async function secureStoreToken(_token) {}
async function clearStoredTokens() {}

export async function getSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error(error.message);
  return session;
}

export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange(callback);
} 